import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { fetchOrganizations, createOrganization as apiCreateOrg } from './api';
import type { Organization } from './types';

const ORG_STORAGE_KEY = 'lc-active-org';

export interface OrgContextValue {
  /** All orgs the current user belongs to */
  orgs: Organization[];
  /** Currently active org (null = no org selected / legacy mode) */
  activeOrg: Organization | null;
  /** Switch to a different org */
  setActiveOrg: (org: Organization | null) => void;
  /** Create a new org and switch to it */
  createOrg: (name: string, slug: string) => Promise<Organization>;
  /** Refresh the org list */
  refresh: () => Promise<void>;
  isLoading: boolean;
}

const OrgCtx = createContext<OrgContextValue | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user, getToken } = useAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [activeOrg, setActiveOrgState] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadOrgs = useCallback(async () => {
    if (!user) {
      setOrgs([]);
      setActiveOrgState(null);
      return;
    }

    const token = await getToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const list = await fetchOrganizations(token);
      setOrgs(list);

      // Restore last active org from localStorage
      const savedId = localStorage.getItem(ORG_STORAGE_KEY);
      const saved = savedId ? list.find((o) => o.id === savedId) : null;

      if (saved) {
        setActiveOrgState(saved);
      } else if (list.length > 0 && !activeOrg) {
        // Auto-select first org if none saved
        setActiveOrgState(list[0]);
        localStorage.setItem(ORG_STORAGE_KEY, list[0].id);
      }
    } catch (err) {
      console.warn('Failed to load organizations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, getToken, activeOrg]);

  useEffect(() => {
    void loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setActiveOrg = useCallback((org: Organization | null) => {
    setActiveOrgState(org);
    if (org) {
      localStorage.setItem(ORG_STORAGE_KEY, org.id);
    } else {
      localStorage.removeItem(ORG_STORAGE_KEY);
    }
  }, []);

  const createOrg = useCallback(
    async (name: string, slug: string): Promise<Organization> => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const org = await apiCreateOrg({ name, slug }, token);
      setOrgs((prev) => [org, ...prev]);
      setActiveOrg(org);
      return org;
    },
    [getToken, setActiveOrg]
  );

  return (
    <OrgCtx.Provider
      value={{
        orgs,
        activeOrg,
        setActiveOrg,
        createOrg,
        refresh: loadOrgs,
        isLoading,
      }}
    >
      {children}
    </OrgCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgCtx);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
}
